import React, { useEffect, useState, useCallback } from 'react';
import { WifiOff, Cloud, CloudUpload, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { subscribe, listQueue, syncNow } from '../lib/offlineQueue';
import { uploadPODPhoto } from '../lib/api';

/**
 * Global helper mounted once in App.js.
 * - Watches queue count and shows a floating badge on mobile when > 0.
 * - Auto-syncs when the browser comes online.
 * - Runs an initial sync attempt on mount (in case the app was closed
 *   while pending items were queued).
 */

const uploadOne = async (entry) => {
  // Reconstruct File from stored Blob so multipart/form-data works
  const file = new File([entry.blob], entry.filename, { type: entry.mime });
  await uploadPODPhoto(entry.dn_id, file, entry.photo_type, entry.notes, entry.gps, entry.item_id);
};

export const OfflineSyncManager = () => {
  const [count, setCount] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [syncing, setSyncing] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const items = await listQueue();
      setCount(items.length);
    } catch {
      // IndexedDB unavailable (private mode etc.) — no-op
    }
  }, []);

  const runSync = useCallback(async (announce = false) => {
    if (syncing) return;
    if (!navigator.onLine) return;
    const items = await listQueue();
    if (!items.length) return;
    setSyncing(true);
    try {
      const { successes, failures } = await syncNow(uploadOne);
      if (announce) {
        if (successes) toast.success(`Synced ${successes} offline upload${successes > 1 ? 's' : ''}`);
        if (failures) toast.error(`${failures} upload${failures > 1 ? 's' : ''} still pending — will retry`);
      }
    } finally {
      setSyncing(false);
      refreshCount();
    }
  }, [syncing, refreshCount]);

  useEffect(() => {
    refreshCount();
    const unsub = subscribe(refreshCount);

    const onOnline = () => {
      setOnline(true);
      toast.info('Back online — syncing offline uploads…');
      runSync(true);
    };
    const onOffline = () => {
      setOnline(false);
      toast.warning('You are offline. Deliveries will be queued locally.');
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    // Attempt an initial sync on mount if we happen to be online with queued items
    setTimeout(() => runSync(false), 1500);

    return () => {
      unsub();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (count === 0 && online) return null;

  return (
    <div
      className="fixed z-40 left-1/2 -translate-x-1/2 bottom-[80px] lg:bottom-6 lg:left-auto lg:right-6 lg:translate-x-0"
      data-testid="offline-sync-badge"
    >
      <button
        onClick={() => runSync(true)}
        disabled={syncing || !online}
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-xs font-semibold transition-all ${
          !online
            ? 'bg-amber-500 text-white'
            : count > 0
            ? 'bg-slate-900 text-white hover:bg-slate-800'
            : 'bg-emerald-600 text-white'
        }`}
      >
        {!online ? (
          <>
            <WifiOff className="h-4 w-4" />
            Offline {count > 0 ? `· ${count} queued` : ''}
          </>
        ) : syncing ? (
          <>
            <CloudUpload className="h-4 w-4 animate-pulse" />
            Syncing… {count} left
          </>
        ) : count > 0 ? (
          <>
            <Cloud className="h-4 w-4" />
            {count} to sync · tap to retry
          </>
        ) : (
          <>
            <CheckCircle2 className="h-4 w-4" />
            All synced
          </>
        )}
      </button>
    </div>
  );
};

export default OfflineSyncManager;
